import React, { FC, useState, useEffect, useContext } from 'react';
import cx from 'classnames';
import { useApolloClient } from '@apollo/react-hooks';
import { NoiseEventsDispatchContext } from 'src/@noiseEvents/providers/NoiseEventsStateProvider';
import { Icons } from '@ems/client-design-system';
import { MapContainer } from 'src/@noiseEvents/containers/MapContainer';
import { useDataSelectors } from 'src/@noiseEvents/reducers';
import { AudioPlayback } from 'src/components/AudioPlayback';
import { fetchInAirData } from 'src/@noiseEvents/actions';
import { ContinuousNoiseGraph } from 'src/components/ContinuousNoiseGraph';
import { PlaybackControl } from 'src/components/PlaybackControl';
// selectors
import { useConfigSelectors, useFilterDataSelectors } from 'src/@app/reducers';
// resolvers
import { fetchContinuousNoiseData } from 'src/@infringements/resolvers/summaryResolver';
// utils
import { addPaddingToDuration } from 'src/utils';
import { getProcessedNoiseData } from 'src/@app/functions/noise';
import { fetchOperationsByIds } from '../resolvers/getDataResolver';
import { IMarkedTime } from '../interfaces';

export const MapPlaybackContainer: FC = () => {
  const client = useApolloClient();
  const dispatcher = useContext(NoiseEventsDispatchContext);
  const [isPlaybackMode, setIsPlaybackMode] = useState<boolean>(false);
  // Playback variables
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [runningStatus, setRunningStatus] = useState<boolean>(true);
  const [draggingStatus, setDraggingStatus] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(0);
  const [currentNoiseData, setCurrentNoiseData] = useState<any[]>([]);
  const [continuousNoiseData, setContinuousNoiseData] = useState<any>([]);
  const [processedNoiseData, setProcessedNoiseData] = useState<any>([]);
  const [pointData, setPointData] = useState<any>([]);
  const dataSelectors = useDataSelectors();
  const { requiredData, inAirTracks, noiseMonitors } = dataSelectors.getRequiredDataForMap();
  // Configuration
  const configSelectors = useConfigSelectors();
  const {
    globals: { noiseSampleType },
  } = configSelectors.getConfig();

  // filters data
  const filtersSelectors = useFilterDataSelectors();
  const operationFilterData = filtersSelectors.getOperationsFilterData();

  useEffect(() => {
    // Don't show playback when >1 is selected for now
    if (requiredData.length !== 1) {
      if (!requiredData.length) {
        setContinuousNoiseData([]);
      }

      setIsPlaybackMode(false);
    }

    if (requiredData.length) {
      const operationIds: number[] = [];
      requiredData.map(currentData => {
        if (!currentData.operationId) {
          return;
        }

        // if it doesn't already exist in the point data
        if (pointData.findIndex(item => item.id === currentData.operationId) === -1) {
          // if it doesn't exist in the accumulator
          if (operationIds.findIndex(item => item === currentData.operationId) === -1) {
            operationIds.push(currentData.operationId);
          }
        }
      }, []);

      // only make the request if there are ids to request
      if (operationIds.length) {
        fetchOperationsByIds(client, operationIds)
          .then((data: any) => {
            setPointData([...pointData, ...data.data]);
          })
          .catch((error: string) => console.error(error));
      }
    }

    if (
      requiredData.length &&
      (!currentNoiseData.length ||
        currentNoiseData[0].id !== requiredData[0].id ||
        currentNoiseData.length !== requiredData.length)
    ) {
      const locationIds: number[] = [];
      requiredData.map((item: any, index: number) => {
        const { locationId } = item;
        locationIds.push(locationId);
      });
      const duration = addPaddingToDuration({
        seconds: 60,
        startTime: requiredData[0].startTime,
        endTime: requiredData[0].endTime,
      });
      fetchContinuousNoiseData(client, locationIds, noiseSampleType, duration)
        .then((data: any) => {
          setContinuousNoiseData(data.data);
        })
        .catch(error => {
          setContinuousNoiseData([]);
          // TODO: handle this error more gracefully - still valid if continuous data is not shown
          // console.error(error);
        });
    }
    setCurrentNoiseData(requiredData);
  }, [requiredData]);

  useEffect(() => {
    if (requiredData.length || continuousNoiseData.length) {
      setProcessedNoiseData(getProcessedNoiseData(requiredData, continuousNoiseData));
    }
  }, [requiredData, continuousNoiseData]);

  useEffect(() => {
    if (currentNoiseData && currentNoiseData.length === 1) {
      fetchInAirData(client, dispatcher, currentNoiseData[0].time, operationFilterData.airportIds);
    }
  }, [currentNoiseData]);

  const hasPlaybackData = requiredData.length === 1;
  const markedTimes: IMarkedTime[] = [];
  requiredData.map(data => {
    if (data.operationId) {
      markedTimes.push({
        time: data.time,
        id: data.operationId,
      });
    }
  });

  return (
    <div className="map_wrapper">
      <div className="map">
        <MapContainer
          currentTime={currentTime}
          isPlaybackMode={isPlaybackMode}
          inAirData={inAirTracks}
          noiseMonitors={noiseMonitors}
          noiseData={currentNoiseData}
          processedNoiseData={processedNoiseData}
          markedTime={markedTimes}
          pointData={pointData}
        />
      </div>
      <div className="playback-slider">
        <ContinuousNoiseGraph
          noiseData={processedNoiseData}
          currentTime={isPlaybackMode ? currentTime : 0}
          shouldDisplay={hasPlaybackData}
          timeBuffer={60}
        />
        {isPlaybackMode && hasPlaybackData && (
          <>
            <AudioPlayback
              audio={currentNoiseData.length ? currentNoiseData[0].audio : undefined}
              isPlaying={isPlaybackMode && runningStatus && !draggingStatus}
              currentTime={currentTime}
              playbackSpeed={playbackSpeed}
            />
            <PlaybackControl
              startTime={requiredData[0].startTime}
              endTime={requiredData[0].endTime}
              markerTime={requiredData[0].time}
              onPositionUpdate={setCurrentTime}
              onPlaybackSpeedUpdate={setPlaybackSpeed}
              onRunningStatusUpdate={setRunningStatus}
              onDraggingStatusUpdate={setDraggingStatus}
              playbackTimeBuffer={60}
              alwaysShowDot={true}
            />
          </>
        )}
      </div>
      <button
        className={cx('mode-playback', {
          'mode-playback--hidden': isPlaybackMode || requiredData.length !== 1,
        })}
        onClick={() => setIsPlaybackMode(!isPlaybackMode)}>
        <Icons iconName={`ic-ui-play`} size="24" fill="#2e384d" />
      </button>
    </div>
  );
};
